import { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import {
  Building2, Hammer, Compass, Wrench, Shield, Hotel,
  Code2, Smartphone, Layers, ChevronDown } from
'lucide-react';

const iconMap = {
  Building2: Building2,
  Hammer: Hammer,
  Compass: Compass,
  Wrench: Wrench,
  Shield: Shield,
  Hotel: Hotel,
  Code2: Code2,
  Smartphone: Smartphone,
  Layers: Layers
};

const companies = [
{
  id: "haveli",
  name: "Haveli",
  tagline: "Premium Home Transformations",
  description: "The real estate and construction arm of Hemraj Grandsons. Focuses on premium home transformations, residential development, renovations, resorts, and future integrated communities.",
  category: "Real Estate & Construction",
  icon: "Building2"
},
{
  id: "horizon",
  name: "Horizon",
  tagline: "Building Materials & Supply",
  description: "The building materials and supply ecosystem. Includes premium hardware, lighting, paints, woodwork, sanitary systems, glass, panelling, steel, kitchens, and future smart infrastructure products.",
  category: "Building Materials",
  icon: "Hammer"
},
{
  id: "kritam",
  name: "Kritam",
  tagline: "Architecture & Interior Design",
  description: "The architecture and interior design company. Designs homes, hotels, commercial spaces, and future townships with a focus on timeless aesthetics and practical execution.",
  category: "Design",
  icon: "Compass"
},
{
  id: "hans",
  name: "HANS",
  tagline: "Property Management",
  description: "Manages and maintains properties after they are built. Provides facility management, maintenance, home services, and operational support for residential and hospitality environments.",
  category: "Services",
  icon: "Wrench"
},
{
  id: "hifazat",
  name: "Hifazat",
  tagline: "Security & Access Management",
  description: "The security and access management arm. Provides physical security, gate management, visitor control, and integrated safety systems.",
  category: "Security",
  icon: "Shield"
},
{
  id: "hemraj-hotels",
  name: "Hemraj Hotels",
  tagline: "Luxury Hospitality",
  description: "The luxury hospitality brand. Owns and operates hotels, resorts, and premium stays designed around Indian warmth, design, and service.",
  category: "Hospitality",
  icon: "Hotel"
},
{
  id: "horizon-codeworks",
  name: "CodeWorks",
  tagline: "Technology Solutions",
  description: "The technology company of Hemraj Grandsons. Builds digital platforms, software systems, and applications for both internal companies and external clients.",
  category: "Technology",
  icon: "Code2"
},
{
  id: "horizon-app",
  name: "Horizon App",
  tagline: "Super App for Living",
  description: "The group's super app for living. Allows users to buy, sell, rent, manage homes, access maintenance, control smart infrastructure, book hotels, manage subscriptions, and interact with the entire Hemraj ecosystem.",
  category: "Technology",
  icon: "Smartphone"
},
{
  id: "hemraj-one",
  name: "Hemraj One",
  tagline: "Internal Operating System",
  description: "The internal operating system of the group. Manages properties, inventory, services, staff, operations, access, and infrastructure across all Hemraj companies.",
  category: "Technology",
  icon: "Layers"
}];


const CompanyCard = ({ company, index, isExpanded, onToggle }) => {
  const IconComponent = iconMap[company.icon] || Building2;

  return (
    <motion.div
      data-testid={`company-card-${company.id}`}
      className="border-b border-black/10 last:border-b-0"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.05 }}>

      <button
        data-testid={`company-toggle-${company.id}`}
        className="w-full py-6 lg:py-8 flex items-center justify-between text-left group"
        onClick={onToggle}>

        <div className="flex items-center gap-6">
          <div className="w-12 h-12 lg:w-14 lg:h-14 bg-[#EFEBE4] flex items-center justify-center transition-colors duration-500 group-hover:bg-[#C5A880]">
            <IconComponent className="w-5 h-5 lg:w-6 lg:h-6 text-[#1A1A1A]" />
          </div>
          <div>
            <h3 className="font-heading text-xl lg:text-2xl font-light text-[#1A1A1A] group-hover:text-[#C5A880] transition-colors duration-300">
              {company.name}
            </h3>
            <p className="text-sm font-medium text-[#4A4A4A] mt-1">{company.tagline}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="hidden sm:block text-xs uppercase tracking-widest text-[#4A4A4A]">
            {company.category}
          </span>
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.3 }}>

            <ChevronDown className="w-5 h-5 text-[#4A4A4A]" />
          </motion.div>
        </div>
      </button>

      <AnimatePresence>
        {isExpanded &&
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden">

            <div className="pb-8 pl-[4.5rem] lg:pl-20 pr-4">
              <p className="text-base font-medium text-[#4A4A4A] leading-relaxed max-w-2xl">
                {company.description}
              </p>
            </div>
          </motion.div>
        }
      </AnimatePresence>
    </motion.div>);

};

export const Companies = () => {
  const [expandedId, setExpandedId] = useState(null);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="companies"
      data-testid="companies-section"
      ref={ref}
      className="py-24 lg:py-32 bg-[#F9F6F0]">

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          <div>
            <motion.p
              className="overline mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}>

              Core Companies
            </motion.p>

            <motion.h2
              data-testid="companies-title"
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#1A1A1A]"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 }}>

              The Companies Behind the Vision
            </motion.h2>
          </div>

          <motion.div
            className="flex items-end"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}>

            <p className="text-base text-[#4A4A4A] leading-relaxed">
              Each company in the Hemraj ecosystem plays a critical role in delivering 
              integrated, premium experiences from land to lifestyle.
            </p>
          </motion.div>
        </div>

        {/* Companies List */}
        <div className="border-t border-black/10">
          {companies.map((company, index) =>
          <CompanyCard
            key={company.id}
            company={company}
            index={index}
            isExpanded={expandedId === company.id}
            onToggle={() => setExpandedId(expandedId === company.id ? null : company.id)} />

          )}
        </div>
      </div>
    </section>);

};